import type {
  ComplimentaryCareEnrollment,
  EnrolleeRelationship,
  EnrollmentStatus,
} from '../api/types';

/** The paper form's own wording for each row of its "New Enrollees" table. */
const relationships: Record<EnrolleeRelationship, string> = {
  teamMember: 'Team Member (You)',
  spouse: 'Spouse',
  domesticPartner: 'Domestic Partner',
  child: 'Child',
};

export const statusLabels: Record<EnrollmentStatus, string> = {
  pendingReview: 'Pending review',
  approved: 'Active',
  rejected: 'Rejected',
  deactivated: 'Deactivated',
};

export function relationshipLabel(relationship: EnrolleeRelationship): string {
  return relationships[relationship];
}

/**
 * The "already receiving care" answer as one line, for every screen that shows it back rather than
 * asking it.
 *
 * Either half can be missing — on a row submitted before the question was split in two, and on any
 * row where the answer was No — so the parts are joined only where they are actually there, rather
 * than printing "at" with nothing after it.
 */
export function describeExistingCare(enrollment: ComplimentaryCareEnrollment): string {
  if (!enrollment.alreadyReceivingCareElsewhere) {
    return 'No';
  }

  const who = enrollment.alreadyReceivingCareWho?.trim() ?? '';
  const clinic = enrollment.alreadyReceivingCareClinic?.trim() ?? '';

  if (who && clinic) {
    return `${who} at ${clinic}`;
  }

  return who || (clinic ? `at ${clinic}` : '') || 'Yes';
}

/**
 * What a row is, for the tag beside the employee's name. Null for a plain enrollment — that is the
 * ordinary case and tagging it would make the tag mean nothing.
 *
 * The two that are tagged are the ones where approving does something beyond approving: it discards
 * the row it replaces. HR should read that off the row, not discover it in the confirmation dialog.
 */
export function submissionKindLabel(enrollment: ComplimentaryCareEnrollment): string | null {
  switch (enrollment.kind) {
    case 'change':
      return 'Change to an active enrollment';
    case 'reEnrollment':
      return 'Re-enrollment after deactivation';
    case 'enrollment':
      return null;
  }
}
